import type { 
  DemoSession, 
  DemoTemplate, 
  Message, 
  LeadFormData, 
  ApiResponse,
  FileInfo 
} from "@/types"

const API_BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000"

class ApiService {
  private async fetch<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`
    
    const config: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return {
          success: false,
          status: response.status,
          error: errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`,
        }
      }

      const data = await response.json()
      return {
        success: true,
        data,
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error occurred",
      }
    }
  }

  // Demo Session APIs
  async startDemo(mode: "upload" | "template"): Promise<ApiResponse<DemoSession>> {
    return this.fetch<DemoSession>("/demo/start", {
      method: "POST",
      body: JSON.stringify({ mode }),
    })
  }

  async getSession(sessionId: string): Promise<ApiResponse<DemoSession>> {
    return this.fetch<DemoSession>(`/demo/session/${sessionId}`)
  }

  // File Upload APIs
  async uploadFile(
    sessionId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<FileInfo>> {
    const formData = new FormData()
    formData.append("files", file)
    formData.append("session_id", sessionId)

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest()
      
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress(progress)
        }
      })

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText)
            resolve({ success: true, data })
          } catch {
            resolve({ success: false, error: "Invalid response format" })
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText)
            resolve({ 
              success: false, 
              error: errorData.detail || errorData.message || `Upload failed: ${xhr.statusText}` 
            })
          } catch {
            resolve({ success: false, error: `Upload failed: ${xhr.statusText}` })
          }
        }
      })

      xhr.addEventListener("error", () => {
        resolve({ success: false, error: "Network error during upload" })
      })

      xhr.addEventListener("abort", () => {
        resolve({ success: false, error: "Upload cancelled" })
      })

      xhr.open("POST", `${API_BASE_URL}/demo/upload`)
      xhr.send(formData)
    })
  }

  async uploadFiles(
    sessionId: string,
    files: File[],
    onFileProgress?: (fileIndex: number, progress: number) => void
  ): Promise<ApiResponse<FileInfo[]>> {
    const results: FileInfo[] = []
    
    for (let i = 0; i < files.length; i++) {
      const result = await this.uploadFile(
        sessionId,
        files[i],
        (progress) => onFileProgress?.(i, progress)
      )
      
      if (result.success && result.data) {
        results.push(result.data)
      } else {
        return {
          success: false,
          error: result.error || `Failed to upload ${files[i].name}`,
          data: results,
        }
      }
    }

    return { success: true, data: results }
  }

  // Template APIs
  async getTemplates(): Promise<ApiResponse<DemoTemplate[]>> {
    return this.fetch<DemoTemplate[]>("/demo/templates")
  }

  async selectTemplate(
    sessionId: string, 
    templateId: string
  ): Promise<ApiResponse<DemoSession>> {
    return this.fetch<DemoSession>("/demo/select-template", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, template_id: templateId }),
    })
  }

  // Chat APIs
  async sendMessage(
    sessionId: string,
    message: string
  ): Promise<ApiResponse<Message>> {
    const result = await this.fetch<{ answer: string; relevant_skills: string[]; message_id: string }>("/chat", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, question: message }),
    })

    if (!result.success || !result.data) {
      return { success: false, error: result.error }
    }

    const msg: Message = {
      id: result.data.message_id,
      role: "assistant",
      content: result.data.answer,
      timestamp: new Date().toISOString(),
      sources: result.data.relevant_skills
        .filter(s => s !== "Unknown")
        .map(s => ({ document: s })),
    }
    return { success: true, data: msg }
  }

  async sendMessageStream(
    sessionId: string,
    message: string,
    onChunk: (chunk: string) => void
  ): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ session_id: sessionId, question: message }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return {
          success: false,
          error: errorData.detail || errorData.message || `HTTP ${response.status}`,
        }
      }

      const reader = response.body?.getReader()
      if (!reader) {
        return { success: false, error: "No response body" }
      }

      const decoder = new TextDecoder()
      
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        
        const chunk = decoder.decode(value, { stream: true })
        onChunk(chunk)
      }

      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Stream error occurred",
      }
    }
  }

  async getChatHistory(sessionId: string): Promise<ApiResponse<Message[]>> {
    return this.fetch<Message[]>(`/chat/history?session_id=${sessionId}`)
  }

  async submitFeedback(
    messageId: string,
    feedback: "positive" | "negative"
  ): Promise<ApiResponse<void>> {
    return this.fetch("/chat/feedback", {
      method: "POST",
      body: JSON.stringify({ message_id: messageId, feedback }),
    })
  }

  // Lead Capture APIs
  async captureLead(
    sessionId: string | null,
    leadData: LeadFormData
  ): Promise<ApiResponse<void>> {
    return this.fetch("/lead/capture", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        ...leadData,
      }),
    })
  }
}

export const api = new ApiService()